import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Heart, LogOut, Menu, X, Globe } from "lucide-react";
import { useState } from "react";

const NavItem = ({ to, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `text-sm font-medium tracking-wide transition-colors ${
        isActive ? "text-[#C85A44]" : "text-[#2C2A28] hover:text-[#C85A44]"
      }`
    }
  >
    {label}
  </NavLink>
);

function LangSwitcher() {
  const { lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid="lang-toggle"
        className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-3 py-2 text-xs inline-flex items-center gap-1.5 transition">
        <Globe className="h-3.5 w-3.5" strokeWidth={1.5} />
        {languages.find((l) => l.code === lang)?.label}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-32 rounded-xl border border-[#E2DCD0] bg-white shadow-lg overflow-hidden z-50" data-testid="lang-menu">
          {languages.map((l) => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
              data-testid={`lang-${l.code}`}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-[#F2EFE9] transition ${lang === l.code ? "text-[#C85A44]" : "text-[#2C2A28]"}`}>
              {l.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#2C2A28]">
      <header className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to={user?.role === "admin" ? "/admin/users" : "/"} data-testid="brand-link" className="flex items-center gap-2 group">
            <Heart className="h-5 w-5 text-[#C85A44] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
            <span className="font-serif-display text-2xl tracking-tight">Sanyog</span>
            {user?.role === "admin" && <span className="ml-2 text-[10px] uppercase tracking-widest bg-[#2C2A28] text-white px-2 py-0.5 rounded-full">Admin</span>}
          </Link>
          {user ? (
            <>
              {user.role === "admin" ? (
                <nav className="hidden md:flex items-center gap-10">
                  <NavItem to="/admin/users" label="Users" testid="nav-admin-users" />
                  <NavItem to="/admin/reports" label="Reports" testid="nav-admin-reports" />
                  <NavItem to="/admin/pricing" label="Pricing" testid="nav-admin-pricing" />
                </nav>
              ) : (
                <nav className="hidden md:flex items-center gap-10">
                  <NavItem to="/browse" label={t("nav.discover")} testid="nav-browse" />
                  <NavItem to="/matches" label={t("nav.matches")} testid="nav-matches" />
                  <NavItem to="/interests" label={t("nav.interests")} testid="nav-interests" />
                  <NavItem to="/dashboard" label={t("nav.dashboard")} testid="nav-dashboard" />
                  <NavItem to="/premium" label={t("nav.premium")} testid="nav-premium" />
                  <NavItem to="/refer" label="Refer" testid="nav-refer" />
                  <NavItem to="/profile/me" label={t("nav.profile")} testid="nav-profile" />
                </nav>
              )}
              <div className="hidden md:flex items-center gap-3">
                <LangSwitcher />
                <span className="overline">{user.full_name}</span>
                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] p-2 transition"
                  aria-label="Logout"
                >
                  <LogOut className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="md:hidden p-2"
                data-testid="mobile-menu-toggle"
                aria-label="Menu"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </>
          ) : (
            <nav className="flex items-center gap-4">
              <LangSwitcher />
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium hover:text-[#C85A44] transition">{t("nav.signin")}</Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] text-white px-5 py-2 text-sm font-medium transition"
              >
                {t("nav.join")}
              </Link>
            </nav>
          )}
        </div>
        {user && menuOpen && (
          <div className="md:hidden border-t border-[#E2DCD0] bg-[#F9F8F6]">
            <div className="px-6 py-4 flex flex-col gap-4">
              {user.role === "admin" ? (
                <>
                  <NavItem to="/admin/users" label="Users" testid="mnav-admin-users" />
                  <NavItem to="/admin/reports" label="Reports" testid="mnav-admin-reports" />
                  <NavItem to="/admin/pricing" label="Pricing" testid="mnav-admin-pricing" />
                </>
              ) : (
                <>
                  <NavItem to="/browse" label={t("nav.discover")} testid="mnav-browse" />
                  <NavItem to="/matches" label={t("nav.matches")} testid="mnav-matches" />
                  <NavItem to="/interests" label={t("nav.interests")} testid="mnav-interests" />
                  <NavItem to="/dashboard" label={t("nav.dashboard")} testid="mnav-dashboard" />
                  <NavItem to="/premium" label={t("nav.premium")} testid="mnav-premium" />
                  <NavItem to="/refer" label="Refer" testid="mnav-refer" />
                  <NavItem to="/profile/me" label={t("nav.profile")} testid="mnav-profile" />
                </>
              )}
              <button onClick={handleLogout} data-testid="mobile-logout" className="text-left text-sm text-[#9C3A3A]">Logout</button>
            </div>
          </div>
        )}
      </header>
      <main>{children}</main>
      <footer className="border-t border-[#E2DCD0] mt-24 py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-serif-display text-xl">Sanyog</div>
          <div className="flex items-center gap-6 text-xs text-[#8C857B]">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#C85A44] transition">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-[#C85A44] transition">Terms of Service</Link>
            <a href="mailto:support@sanyogvivah.in" className="hover:text-[#C85A44] transition">Contact</a>
          </div>
          <div className="overline">Crafted with intention · A modern matrimony</div>
        </div>
      </footer>
    </div>
  );
}
